/**
 * Admin Authentication Store
 * Manages admin authentication state separately from customer auth
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Admin } from '@/types/admin';

interface AdminAuthState {
  admin: Admin | null;
  token: string | null;
  isAuthenticated: boolean;

  // Actions
  setAdmin: (admin: Admin, token: string) => void;
  clearAuth: () => void;
  updateAdmin: (admin: Partial<Admin>) => void;
  setSelectedRestaurant: (restaurantId: string) => void;
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set) => ({
      admin: null,
      token: null,
      isAuthenticated: false,

      setAdmin: (admin, token) => {
        set({
          admin,
          token,
          isAuthenticated: true,
        });
      },

      clearAuth: () => {
        set({
          admin: null,
          token: null,
          isAuthenticated: false,
        });
      },

      updateAdmin: (adminUpdate) => {
        set((state) => ({
          admin: state.admin ? { ...state.admin, ...adminUpdate } : null,
        }));
      },

      setSelectedRestaurant: (restaurantId) => {
        set((state) => ({
          admin: state.admin ? { ...state.admin, selected_restaurant_id: restaurantId } : null,
        }));
      },
    }),
    {
      name: 'kombu-admin-auth-storage',
      // Only persist admin and token, not isAuthenticated (will be derived)
      partialize: (state) => ({
        admin: state.admin,
        token: state.token,
        isAuthenticated: state.admin !== null && state.token !== null,
      }),
    }
  )
);

// Helper hook for easier access
export const useAdminAuth = () => {
  const admin = useAdminAuthStore((state) => state.admin);
  const token = useAdminAuthStore((state) => state.token);
  const isAuthenticated = useAdminAuthStore((state) => state.isAuthenticated);
  const setAdmin = useAdminAuthStore((state) => state.setAdmin);
  const clearAuth = useAdminAuthStore((state) => state.clearAuth);
  const updateAdmin = useAdminAuthStore((state) => state.updateAdmin);
  const setSelectedRestaurant = useAdminAuthStore((state) => state.setSelectedRestaurant);

  return {
    admin,
    token,
    isAuthenticated,
    setAdmin,
    clearAuth,
    updateAdmin,
    setSelectedRestaurant,
  };
};
